import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { 
  checkNotificationSupport, 
  requestNotificationPermission, 
  notifyClockEvent 
} from '../utils/notifications';
import { toast } from 'sonner';
import axios from 'axios';
import { subscribeRealtime } from '../lib/realtime';

const NotificationContext = createContext(null);
const NOTIFICATIONS_ENABLED_KEY = 'notifications_enabled';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function NotificationProvider({ children }) {
  const { user, token } = useAuth();
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem(NOTIFICATIONS_ENABLED_KEY);
    return saved !== 'false'; // Default to true if not set
  });
  const [lastCheckedId, setLastCheckedId] = useState(null);
  const pollingRef = useRef(null);

  // Request notification permission for admin
  useEffect(() => {
    if (user?.role === 'admin' && checkNotificationSupport() && notificationsEnabled) {
      requestNotificationPermission().then(granted => {
        setPermissionGranted(granted);
        if (granted) {
          toast.success('Push notifications enabled!');
        }
      });
    }
  }, [user, notificationsEnabled]);

  // Poll for new notifications (admin only)
  const checkNewNotifications = useCallback(async () => {
    if (!token || user?.role !== 'admin' || !notificationsEnabled) return;

    try {
      const response = await axios.get(`${API}/notifications`, {
        params: { unread_only: true, limit: 10 },
        headers: { Authorization: `Bearer ${token}` }
      });

      const notifications = response.data;
      
      if (notifications.length > 0) {
        // Check for new notifications we haven't seen
        notifications.forEach(notif => {
          if (lastCheckedId && notif.id !== lastCheckedId) {
            // This is a new notification - send push
            if (notif.type === 'clock_in' || notif.type === 'clock_out') {
              notifyClockEvent(notif.employee_name, notif.type);
            }
          }
        });
        
        // Update last checked ID
        if (notifications[0]) {
          setLastCheckedId(notifications[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to check notifications:', error);
    }
  }, [token, user, lastCheckedId, notificationsEnabled]);

  // Start polling when admin logs in
  useEffect(() => {
    if (user?.role === 'admin' && permissionGranted && notificationsEnabled) {
      // Initial check
      checkNewNotifications();
      
      // Poll every 10 seconds
      pollingRef.current = setInterval(checkNewNotifications, 10000);
      
      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    }
  }, [user, permissionGranted, checkNewNotifications, notificationsEnabled]);

  // Instant sync: check notifications immediately after any clock mutation.
  useEffect(() => {
    if (user?.role !== 'admin' || !notificationsEnabled) return undefined;
    return subscribeRealtime((msg) => {
      if (msg?.type === 'sync' && (msg.path || '').includes('/clock')) checkNewNotifications();
    });
  }, [user, notificationsEnabled, checkNewNotifications]);

  const requestPermission = async () => {
    const granted = await requestNotificationPermission();
    setPermissionGranted(granted);
    return granted;
  };

  const toggleNotifications = (enabled) => {
    setNotificationsEnabled(enabled);
    localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, String(enabled));
    if (!enabled) {
      // Stop polling when disabled
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  };

  return (
    <NotificationContext.Provider value={{ 
      permissionGranted, 
      requestPermission,
      isSupported: checkNotificationSupport(),
      notificationsEnabled,
      toggleNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
